import { Box, Button, Radio, Typography } from '@mui/material';
import { ArrowsInSimple, Chats, Code, Copy } from '@phosphor-icons/react';
import { useState } from 'react';
import { useNotificationStore } from '../../../store/storeNotifications';

interface EmbedOptionProps {
  chatData: any;
}

export function EmbedOption({ chatData }: EmbedOptionProps) {
  const [typeEmbed, setTypeEmbed] = useState(true);
  const setNotification = useNotificationStore(state => state.setNotification);
  const [selectedValue, setSelectedValue] = useState('standard');

  function handleTypeEmbed() {
    setTypeEmbed(!typeEmbed);
  }

  function handleCopy(type: string) {
    switch (type) {
      case 'iframe':
        navigator.clipboard.writeText(iframeCode).then(() => {
          setNotification({
            type: 'success',
            message: 'Iframe copy',
          });
        });
        break;

      case 'script':
        navigator.clipboard.writeText(scriptCode).then(() => {
          setNotification({
            type: 'success',
            message: 'Script copy',
          });
        });
        break;

      default:
        setNotification({
          type: 'error',
          message: 'Error copying content',
        });
    }
  }

  const handleChangeEmbed = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const iframeCode = `<iframe src='${window.location.origin}/chatbot/${chatData.id}' height='800px' width='600px' style="border: none;"></iframe>`;
  const scriptCode = `<script src='${window.location.origin}/embed-script.js'></script>
<flux-prompt-chat src='${window.location.origin}/chatbot/${chatData.id}' width="600px" height="800px"></flux-prompt-chat>`;

  const iframePopUp = `<iframe 
    src='${window.location.origin}/chatbot/${chatData.id}' 
    height='800px' 
    width='600px' 
    style="
        position: fixed; 
        bottom: 20px; 
        right: 20px; 
        z-index: 9999; 
        border: none; 
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    ">
</iframe>`;
  const scriptPopUp = `<script src='${window.location.origin}/embed-script.js'></script>
  <flux-prompt-chat 
      src='${window.location.origin}/chatbot/${chatData.id}' 
      width="600px" 
      height="800px" 
      style="
          position: fixed; 
          bottom: 20px; 
          right: 20px; 
          z-index: 9999; 
          box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1); 
      ">
  </flux-prompt-chat>`;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      width="100%"
      height="600px"
    >
      <Box display="flex" flexDirection="column" gap="16px">
        <Box display="flex" alignItems="center" gap="8px" width="100%" mb="8px">
          <Code size={20} color="#101828" />
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: '500',
              color: '#101828',
            }}
          >
            Embed Options
          </Typography>
        </Box>

        <Typography
          sx={{ fontSize: '16px', fontWeight: '500', color: '#101828' }}
        >
          {`Embed ${chatData.name === null ? '' : chatData.name}`}
        </Typography>

        <Box display="flex" justifyContent="space-between" gap="24px">
          <Box
            display="flex"
            flexDirection="column"
            gap="16px"
            padding="20px"
            borderRadius="16px"
            border={`1px solid ${
              selectedValue === 'standard' ? '#3650DE' : '#D0D5DD'
            }`}
            width="100%"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" gap="16px" alignItems="center">
                <Chats
                  size={32}
                  color={selectedValue === 'standard' ? '#3650DE' : '#475467'}
                />
                <Typography
                  sx={{
                    fontWeight: '500',
                    fontSize: '20px',
                    color: selectedValue === 'standard' ? '#3650DE' : '#475467',
                  }}
                >
                  Standard
                </Typography>
              </Box>

              <Radio
                checked={selectedValue === 'standard'}
                onChange={handleChangeEmbed}
                value="standard"
                name="radio-buttons"
              />
            </Box>
            <Typography
              sx={{ color: '#475467', fontWeight: '400', fontSize: '16px' }}
            >
              Embed directly on a web page.
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            gap="16px"
            padding="20px"
            borderRadius="16px"
            border={`1px solid ${
              selectedValue === 'pop-up' ? '#3650DE' : '#D0D5DD'
            }`}
            width="100%"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" gap="16px" alignItems="center">
                <ArrowsInSimple
                  size={32}
                  color={selectedValue === 'pop-up' ? '#3650DE' : '#475467'}
                />
                <Typography
                  sx={{
                    fontWeight: '500',
                    fontSize: '20px',
                    color: selectedValue === 'pop-up' ? '#3650DE' : '#475467',
                  }}
                >
                  Pop-up
                </Typography>
              </Box>

              <Radio
                checked={selectedValue === 'pop-up'}
                onChange={handleChangeEmbed}
                value="pop-up"
                name="radio-buttons"
              />
            </Box>
            <Typography
              sx={{ color: '#475467', fontWeight: '400', fontSize: '16px' }}
            >
              Overlay and fix to the bottom right of a web page.
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{ fontSize: '16px', fontWeight: '500', color: '#101828' }}
        >
          Code snippet
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          gap="24px"
          padding="24px"
          borderRadius="16px"
          border="1px solid #D0D5DD"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" gap="4px">
              <Button
                variant={typeEmbed ? 'contained' : 'outlined'}
                onClick={handleTypeEmbed}
              >
                Script
              </Button>
              <Button
                variant={!typeEmbed ? 'contained' : 'outlined'}
                onClick={handleTypeEmbed}
              >
                iframe
              </Button>
            </Box>

            <Button
              variant="text"
              sx={{ gap: '8px' }}
              onClick={() => handleCopy(typeEmbed ? 'script' : 'iframe')}
            >
              Copy code <Copy size={20} />
            </Button>
          </Box>

          {typeEmbed && (
            <Typography
              sx={{
                color: '#475467',
                fontSize: '16px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                flex: 1,
              }}
            >
              {selectedValue === 'standard' ? scriptCode : scriptPopUp}
            </Typography>
          )}

          {!typeEmbed && (
            <Typography
              sx={{
                color: '#475467',
                fontSize: '16px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                flex: 1,
              }}
            >
              {selectedValue === 'standard' ? iframeCode : iframePopUp}
            </Typography>
          )}
        </Box>
      </Box>

      {/* <Box display="flex" justifyContent="end" width="100%">
        <Button variant="contained" sx={{ width: '160px' }}>
          Save changes
        </Button>
      </Box> */}
    </Box>
  );
}
